import { useEffect, useState } from "react";
import { C } from "../theme";
import { MEMBER_ENROLLMENT_TYPE, memberEnrollmentTypeLabel } from "../lib/enums";
import { ApiError } from "../lib/api/client";
import { createCheckIn, getCheckInSummary, getDailyCheckInStatuses, searchMembersAutocomplete } from "../lib/api/endpoints";
import { useSession } from "../lib/session/SessionContext";
import type { DailyCheckInStatusDto, CheckInSummaryDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { FieldInput } from "./Field";
import { KpiCard } from "./KpiCard";
import { Label } from "./Label";
import { MembershipMembersTab } from "./MembershipMembersTab";

const CHECK_IN_STATUS = { CheckedIn: 1 } as const;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "Este aluno já possui check-in registrado hoje.";
    return error.detail ?? error.message;
  }
  return "Não foi possível concluir o check-in.";
}

export function WellhubCheckInsTab() {
  const { accessToken } = useSession();
  const token = accessToken ?? "";
  const [statuses, setStatuses] = useState<DailyCheckInStatusDto[] | null>(null);
  const [summary, setSummary] = useState<CheckInSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<DailyCheckInStatusDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingInMemberId, setCheckingInMemberId] = useState<string | null>(null);
  const date = today();

  const load = () => {
    if (!token) return;
    setError(null);
    Promise.all([
      getDailyCheckInStatuses(token, { date, page: 1, pageSize: 100 }),
      getCheckInSummary(token, { date }),
    ])
      .then(([daily, dailySummary]) => {
        setStatuses(daily.items);
        setSummary(dailySummary);
      })
      .catch(loadError => setError(errorMessage(loadError)));
  };

  useEffect(load, [token, date]);

  const selectBySearch = async () => {
    const term = search.trim();
    if (term.length < 2 || !token) return;
    try {
      const matches = await searchMembersAutocomplete(token, { q: term, limit: 8 });
      const eligible = matches
        .map(match => statuses?.find(status => status.memberId === match.id))
        .find((status): status is DailyCheckInStatusDto => status !== undefined);
      if (!eligible) {
        setError("Selecione um aluno Wellhub ou TotalPass elegível para check-in.");
        return;
      }
      setSelected(eligible);
      setError(null);
    } catch (searchError) {
      setError(errorMessage(searchError));
    }
  };

  const submit = async () => {
    if (!selected || selected.status === CHECK_IN_STATUS.CheckedIn) return;
    await submitMember(selected.memberId, selected.classId);
    setSelected(null);
    setSearch("");
  };

  const submitMember = async (memberId: string, classId: string | null) => {
    setSubmitting(true);
    setCheckingInMemberId(memberId);
    setError(null);
    try {
      await createCheckIn(token, { memberId, classId, checkInDate: date, notes: null });
      load();
    } catch (submitError) {
      setError(errorMessage(submitError));
    } finally {
      setSubmitting(false);
      setCheckingInMemberId(null);
    }
  };

  const checkedIn = (summary?.wellhub.checkedIn ?? 0) + (summary?.totalpass.checkedIn ?? 0);
  const checkedInStatuses = statuses?.filter(status => status.status === CHECK_IN_STATUS.CheckedIn) ?? [];

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
        <KpiCard label="Check-ins validados hoje" value={String(checkedIn)} sub="registros efetivados" highlight />
        <KpiCard label="Wellhub" value={String(summary?.wellhub.checkedIn ?? 0)} sub="check-ins validados" />
        <KpiCard label="TotalPass" value={String(summary?.totalpass.checkedIn ?? 0)} sub="check-ins validados" />
      </div>

      <Card style={{ marginTop: 12 }}>
        <Label>Check-in manual</Label>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <FieldInput aria-label="Buscar aluno para check-in" placeholder="Busque por nome, matrícula ou documento" value={search} onChange={event => setSearch(event.target.value)} style={{ flex: 1, minWidth: 220 }} />
          <Button onClick={selectBySearch} disabled={search.trim().length < 2}>Buscar aluno</Button>
        </div>
        {selected && (
          <div style={{ marginTop: 12, padding: 12, background: C.limeSoft, borderRadius: 8 }}>
            <b style={{ color: C.ink }}>{selected.memberName}</b>
            <div style={{ color: C.gray, fontSize: 12, marginTop: 3 }}>{memberEnrollmentTypeLabel(selected.enrollmentType)} · {selected.planName} · {selected.className ?? "Sem turma"}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <Button onClick={submit} disabled={submitting || selected.status === CHECK_IN_STATUS.CheckedIn}>{submitting ? "Validando..." : "Confirmar check-in"}</Button>
              <Button variant="ghost" onClick={() => setSelected(null)}>Cancelar</Button>
            </div>
          </div>
        )}
      </Card>

      <MembershipMembersTab
        onConfirmCheckIn={member => {
          const status = statuses?.find(item => item.memberId === member.id);
          void submitMember(member.id, status?.classId ?? null);
        }}
        checkingInMemberId={checkingInMemberId}
        checkedInMemberIds={checkedInStatuses.map(status => status.memberId)}
      />

      {error && <Card style={{ marginTop: 12, borderColor: C.red }}><span style={{ color: C.red }}>{error}</span><Button variant="ghost" onClick={load} style={{ marginLeft: 8 }}>Tentar novamente</Button></Card>}

      <Card style={{ marginTop: 12, overflowX: "auto" }}>
        <Label>Check-ins validados hoje</Label>
        {statuses === null ? <div style={{ color: C.light, padding: "18px 4px" }}>Carregando check-ins...</div> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
            <thead><tr style={{ textAlign: "left", color: C.light, fontSize: 11 }}><th>ALUNO</th><th>ORIGEM</th><th>TURMA</th><th>STATUS</th></tr></thead>
            <tbody>{checkedInStatuses.map(status => <tr key={status.memberId} style={{ borderTop: `1px solid ${C.faint}` }}>
              <td style={{ padding: "10px 4px" }}><b style={{ color: C.ink }}>{status.memberName}</b><div style={{ color: C.light, fontSize: 11 }}>{status.planName}</div></td>
              <td>{memberEnrollmentTypeLabel(status.enrollmentType)}</td>
              <td>{status.className ?? "-"}</td>
              <td><span style={{ fontWeight: 700 }}>✓ Validado</span></td>
            </tr>)}</tbody>
          </table>
        )}
      </Card>
    </>
  );
}
